import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { HeartPulse, AlertTriangle, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Page publique URGENCE — accessible via le QR code médical d'un utilisateur.
 * AUCUNE authentification requise : un médecin/urgentiste qui scanne le QR
 * doit voir les infos vitales en 1 seconde.
 */
export default function MedicalUrgence() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/medical/share/${token}`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Fiche introuvable");
      } finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-red-700 text-white flex items-center justify-center">
      <Loader2 className="h-12 w-12 animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-red-700 text-white flex flex-col items-center justify-center p-6">
      <AlertTriangle className="h-16 w-16 mb-4" />
      <h1 className="text-2xl font-serif-display mb-2">Fiche introuvable</h1>
      <p className="opacity-80">{error}</p>
    </div>
  );

  const Field = ({ label, value, urgent }) => {
    if (!value || (Array.isArray(value) && value.length === 0)) return null;
    const text = Array.isArray(value) ? value.join(" · ") : String(value);
    return (
      <div className={`p-4 rounded-sm ${urgent ? "bg-red-50 border-2 border-red-600" : "bg-white border border-slate-200"}`}>
        <p className="text-[10px] uppercase tracking-[0.2em] text-slate-600 mb-1">{label}</p>
        <p className={`text-lg font-bold ${urgent ? "text-red-900" : "text-slate-900"}`} data-testid={`urgence-${label.toLowerCase().replace(/[^a-z]/g, '')}`}>{text}</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-red-700 text-white">
      <div className="bg-red-800 p-6 text-center">
        <HeartPulse className="h-10 w-10 mx-auto mb-2" />
        <h1 className="text-2xl font-serif-display font-bold">FICHE MÉDICALE D'URGENCE</h1>
        <p className="text-sm opacity-90">Un Avocat en Or — Dossier d'urgence vérifié</p>
      </div>

      <div className="bg-white text-slate-900 p-4 space-y-3">
        <Field label="Allergies (DANGER)" value={data.allergies} urgent />
        <Field label="Traitements en cours" value={data.current_meds} />
        <Field label="Conditions chroniques" value={data.conditions} />
        <Field label="Groupe sanguin" value={data.blood_type} />
        <Field label="Contact d'urgence" value={data.emergency_contact} />
        <Field label="Langues parlées" value={data.languages_spoken} />
        {data.organ_donor === true && (
          <div className="bg-emerald-50 border-2 border-emerald-600 p-4 rounded-sm">
            <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 mb-1">Donneur d'organes</p>
            <p className="text-lg font-bold text-emerald-900">OUI — Volonté exprimée</p>
          </div>
        )}
        {data.updated_at && (
          <p className="text-xs text-slate-500 text-center pt-3">
            Dossier mis à jour le {new Date(data.updated_at).toLocaleDateString("fr-BE")}
          </p>
        )}
      </div>
    </div>
  );
}
